import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type PlanStatus = 'draft' | 'pending_approval' | 'approved' | 'active' | 'closed' | 'cancelled' | 'rejected';

export interface PlanItem {
  id: number;
  productId: number;
  plannedQty: number;
  producedQty: number;
  remarks: string | null;
}

export interface ProductionCommitment {
  id: number;
  planId: number;
  planItemId: number | null;
  productId: number;
  commitDate: string;
  committedQty: number;
  producedQty: number;
  status: string;
  remarks: string | null;
}

export interface ProductionPlan {
  id: number;
  planCode: string;
  title: string;
  periodStart: string | null;
  periodEnd: string | null;
  regionId: number | null;
  status: PlanStatus;
  totalPlannedQty: number;
  remarks: string | null;
  rejectionReason: string | null;
  createdAt: string;
  items?: PlanItem[];
  commitments?: ProductionCommitment[];
}

export interface CreatePlanInput {
  title: string;
  periodStart?: string;
  periodEnd?: string;
  regionId?: number | null;
  remarks?: string;
  items: { productId: number; plannedQty: number; remarks?: string }[];
}

export interface StockHandover {
  id: number;
  handoverCode: string;
  productId: number;
  inventoryItemId: number | null;
  warehouseId: number;
  commitmentId: number | null;
  planId: number | null;
  quantity: number;
  unitCost: number;
  inventoryTxnId: number | null;
  handedOverBy: string | null;
  receivedBy: string | null;
  status: string;
  remarks: string | null;
  createdAt: string;
}

export interface CreateHandoverInput {
  productId: number;
  warehouseId: number;
  quantity: number;
  unitCost?: number;
  commitmentId?: number | null;
  planId?: number | null;
  handedOverBy?: string;
  receivedBy?: string;
  remarks?: string;
}

export interface ProductionDashboard {
  totalPlans: number;
  byStatus: Record<string, number>;
  pendingApproval: number;
  approved: number;
  totalPlanned: number;
  totalProduced: number;
  pendingHandovers: number;
  recent: ProductionPlan[];
}

const planBase = createCrudApi<ProductionPlan, CreatePlanInput>('production-plans');

export const productionPlansApi = {
  ...planBase,
  async dashboard(): Promise<ProductionDashboard> {
    return (await apiClient.get<ApiSuccess<ProductionDashboard>>('/production-plans/dashboard')).data.data;
  },
  async submit(id: number): Promise<ProductionPlan> {
    return (await apiClient.post<ApiSuccess<ProductionPlan>>(`/production-plans/${id}/submit`, {})).data.data;
  },
  async approve(id: number): Promise<ProductionPlan> {
    return (await apiClient.post<ApiSuccess<ProductionPlan>>(`/production-plans/${id}/approve`, {})).data.data;
  },
  async reject(id: number, reason: string): Promise<ProductionPlan> {
    return (await apiClient.post<ApiSuccess<ProductionPlan>>(`/production-plans/${id}/reject`, { reason })).data.data;
  },
};

export const commitmentsApi = {
  async list(params: Record<string, unknown> = {}) {
    const { data } = await apiClient.get<ApiSuccess<ProductionCommitment[]>>('/production-commitments', { params });
    return { data: data.data, meta: data.meta };
  },
  async create(input: { planId: number; planItemId?: number | null; productId: number; commitDate: string; committedQty: number; remarks?: string }): Promise<ProductionCommitment> {
    return (await apiClient.post<ApiSuccess<ProductionCommitment>>('/production-commitments', input)).data.data;
  },
  async produce(id: number, producedQty: number): Promise<ProductionCommitment> {
    return (await apiClient.post<ApiSuccess<ProductionCommitment>>(`/production-commitments/${id}/produce`, { producedQty })).data.data;
  },
};

export const handoversApi = {
  ...createCrudApi<StockHandover, CreateHandoverInput>('stock-handovers'),
  async complete(id: number): Promise<StockHandover> {
    return (await apiClient.post<ApiSuccess<StockHandover>>(`/stock-handovers/${id}/complete`, {})).data.data;
  },
};
